import type { Database } from "@base-hormonal/database";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";

type ProtocolRow = Database["public"]["Tables"]["protocolos"]["Row"];
type ProtocolItemRow = Database["public"]["Tables"]["protocolo_itens"]["Row"];
type StatusHistoryRow = Database["public"]["Tables"]["protocolo_status_historico"]["Row"];

export interface Protocol extends ProtocolRow {
  protocolo_itens: ProtocolItemRow[];
  protocolo_status_historico: Pick<
    StatusHistoryRow,
    "status_anterior" | "status_novo" | "alterado_em"
  >[];
}

export const PROTOCOL_PAGE_SIZE = 20;

/** Linha enxuta para sobrepor períodos de protocolo a outras séries temporais. */
export interface ProtocolTimelineRow
  extends Pick<
    ProtocolRow,
    | "id"
    | "titulo"
    | "status"
    | "versao"
    | "data_inicio"
    | "data_fim_prevista"
    | "created_at"
    | "updated_at"
  > {
  protocolo_status_historico: Pick<
    StatusHistoryRow,
    "status_anterior" | "status_novo" | "alterado_em"
  >[];
}

export const PROTOCOL_TIMELINE_LIMIT = 200;

const PROTOCOL_TIMELINE_COLUMNS =
  "id,titulo,status,versao,data_inicio,data_fim_prevista,created_at,updated_at," +
  "protocolo_status_historico!protocolo_status_historico_protocolo_id_fkey(status_anterior,status_novo,alterado_em)";

const PROTOCOL_COLUMNS =
  "id,aluno_id,user_id,titulo,objetivo,status,data_inicio,data_fim_prevista,grupo_versao_id,versao,protocolo_anterior_id,created_at,updated_at," +
  "protocolo_itens!protocolo_itens_protocolo_id_fkey(id,protocolo_id,substancia,dose,dose_texto_original,dose_valor,dose_unidade,via,frequencia,frequencia_tipo,frequencia_intervalo_dias,frequencia_dias_semana,primeira_aplicacao_em,horario,fase,observacoes,ordem,arquivado_em)," +
  "protocolo_status_historico!protocolo_status_historico_protocolo_id_fkey(status_anterior,status_novo,alterado_em)";

export interface ProtocolItemInput {
  /** Presente quando o item já existe e deve ser atualizado em vez de criado. */
  originalId?: string | null;
  substancia: string;
  dose: string | null;
  dose_valor: number | null;
  dose_unidade: string | null;
  via: string | null;
  frequencia: string | null;
  frequencia_tipo: string;
  frequencia_intervalo_dias: number | null;
  frequencia_dias_semana: number[] | null;
  primeira_aplicacao_em: string | null;
  horario: string | null;
  fase: string | null;
  observacoes: string | null;
}

export interface ProtocolInput {
  id?: string | null;
  /** Versionamento: o protocolo base cuja próxima versão está sendo criada. */
  baseId?: string | null;
  aluno_id: string;
  titulo: string;
  objetivo: string | null;
  status: string;
  data_inicio: string | null;
  data_fim_prevista: string | null;
  itens: ProtocolItemInput[];
}

export interface ProtocolValues {
  titulo: string;
  status: string;
  objetivo: string | null;
  data_inicio: string | null;
  data_fim_prevista: string | null;
  updated_at: string;
}

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

/** Normaliza a frequência estruturada: cada tipo só carrega os campos que usa. */
function normalizeItem(item: ProtocolItemInput, ordem: number) {
  const isInterval = item.frequencia_tipo === "intervalo_dias";
  const isWeekdays = item.frequencia_tipo === "dias_semana";
  return {
    substancia: item.substancia,
    dose: item.dose,
    // A dose digitada é preservada literalmente; valor/unidade são derivados.
    dose_texto_original: item.dose,
    dose_valor: item.dose_valor,
    dose_unidade: item.dose_unidade,
    via: item.via,
    frequencia: item.frequencia,
    frequencia_tipo: item.frequencia_tipo,
    frequencia_intervalo_dias: isInterval ? item.frequencia_intervalo_dias : null,
    frequencia_dias_semana: isWeekdays ? item.frequencia_dias_semana : null,
    primeira_aplicacao_em: null,
    horario: isWeekdays ? item.horario : null,
    fase: item.fase,
    observacoes: item.observacoes,
    ordem,
  };
}

export function protocolInputIssues(input: ProtocolInput): string[] {
  const issues: string[] = [];
  if (input.titulo.trim() === "") issues.push("Informe o título do protocolo.");
  if (input.itens.length === 0) issues.push("Informe pelo menos uma substância.");
  for (const item of input.itens) {
    if (item.substancia.trim() === "") {
      issues.push("Toda substância precisa de nome.");
      continue;
    }
    if ((item.dose_valor === null) !== (item.dose_unidade === null)) {
      issues.push(`Revise dose e unidade de ${item.substancia}.`);
    }
    if (
      item.frequencia_tipo === "intervalo_dias" &&
      item.frequencia_intervalo_dias === null
    ) {
      issues.push(`Informe o intervalo de ${item.substancia}.`);
    }
    if (
      item.frequencia_tipo === "dias_semana" &&
      (item.frequencia_dias_semana ?? []).length === 0
    ) {
      issues.push(`Selecione os dias da semana de ${item.substancia}.`);
    }
  }
  return [...new Set(issues)];
}

/**
   * Escritor único de protocolos e itens. Versão nova nunca altera a
 * anterior além de pausá-la, e a pausa é compensada quando a inserção falha.
 */
export function createProtocolsApi(client: TypedSupabaseClient) {
  return {
    async list(profileId: string, page = 0, signal?: AbortSignal): Promise<Protocol[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("protocolos")
          .select(PROTOCOL_COLUMNS)
          .eq("aluno_id", profileId)
          .order("updated_at", { ascending: false })
          .range(page * PROTOCOL_PAGE_SIZE, (page + 1) * PROTOCOL_PAGE_SIZE - 1)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as Protocol[];
      });
    },

    /** Protocolos de todas as versões, sem paginação, para datar períodos. */
    async listForTimeline(profileId: string, signal?: AbortSignal): Promise<ProtocolTimelineRow[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("protocolos")
          .select(PROTOCOL_TIMELINE_COLUMNS)
          .eq("aluno_id", profileId)
          .order("created_at", { ascending: false })
          .limit(PROTOCOL_TIMELINE_LIMIT)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as ProtocolTimelineRow[];
      });
    },

    async saveProtocol(userId: string, input: ProtocolInput, known: Protocol[]): Promise<string> {
      const issues = protocolInputIssues(input);
      if (issues.length > 0) throw new PublicAppError("invalid_input", issues[0] ?? "");
      const values = {
        titulo: input.titulo.trim(),
        status: input.status,
        objetivo: input.objetivo,
        data_inicio: input.data_inicio,
        data_fim_prevista: input.data_fim_prevista,
        updated_at: new Date().toISOString(),
      };
      if (input.id != null && input.id !== "")
        return this.updateProtocol(input.id, values, input, known);
      return this.createVersion(userId, values, input, known);
    },

    async updateProtocol(
      protocolId: string,
      values: ProtocolValues,
      input: ProtocolInput,
      known: Protocol[],
    ): Promise<string> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("protocolos")
          .update(values)
          .eq("id", protocolId)
          .abortSignal(signal);
        raise(error);
      });
      for (const [ordem, item] of input.itens.entries()) {
        const payload = normalizeItem(item, ordem);
        await withRequestSignal(undefined, async (signal) => {
          const result =
            item.originalId != null && item.originalId !== ""
              ? await client
                  .from("protocolo_itens")
                  .update({ ...payload, arquivado_em: null })
                  .eq("id", item.originalId)
                  .eq("protocolo_id", protocolId)
                  .abortSignal(signal)
              : await client
                  .from("protocolo_itens")
                  .insert({ ...payload, protocolo_id: protocolId })
                  .abortSignal(signal);
          raise(result.error);
        });
      }
      // Item retirado do formulário é arquivado para preservar as versões anteriores.
      const original = known.find((protocol) => protocol.id === protocolId);
      const kept = new Set(
        input.itens.map((item) => item.originalId).filter((id): id is string => id != null),
      );
      const archived = (original?.protocolo_itens ?? [])
        .map((item) => item.id)
        .filter((id) => !kept.has(id));
      if (archived.length > 0) {
        await withRequestSignal(undefined, async (signal) => {
          const { error } = await client
            .from("protocolo_itens")
            .update({ arquivado_em: new Date().toISOString() })
            .in("id", archived)
            .eq("protocolo_id", protocolId)
            .abortSignal(signal);
          raise(error);
        });
      }
      return protocolId;
    },

    async createVersion(
      userId: string,
      values: ProtocolValues,
      input: ProtocolInput,
      known: Protocol[],
    ): Promise<string> {
      const base =
        input.baseId == null ? null : (known.find((item) => item.id === input.baseId) ?? null);
      let pausedBase: Protocol | null = null;
      try {
        if (base !== null && base.status === "ativo" && input.status === "ativo") {
          await withRequestSignal(undefined, async (signal) => {
            const { error } = await client
              .from("protocolos")
              .update({ status: "pausado", updated_at: new Date().toISOString() })
              .eq("id", base.id)
              .abortSignal(signal);
            raise(error);
          });
          pausedBase = base;
        }
        const versions = known
          .filter((item) => base !== null && item.grupo_versao_id === base.grupo_versao_id)
          .map((item) => Number(item.versao) || 1);
        const created = await withRequestSignal(undefined, async (signal) => {
          const { data, error } = await client
            .from("protocolos")
            .insert({
              ...values,
              aluno_id: input.aluno_id,
              user_id: userId,
              ...(base === null ? {} : { grupo_versao_id: base.grupo_versao_id }),
              versao: base === null ? 1 : Math.max(...versions, 1) + 1,
              protocolo_anterior_id: base?.id ?? null,
            })
            .select("id")
            .abortSignal(signal)
            .single();
          raise(error);
          if (data === null)
            throw new PublicAppError("empty_response", "O protocolo não foi salvo.");
          return data;
        });
        const rows = input.itens.map((item, ordem) => ({
          ...normalizeItem(item, ordem),
          protocolo_id: created.id,
        }));
        try {
          await withRequestSignal(undefined, async (signal) => {
            const { error } = await client.from("protocolo_itens").insert(rows).abortSignal(signal);
            raise(error);
          });
        } catch (error: unknown) {
          // Protocolo sem item não é um estado válido do domínio.
          await client.from("protocolos").delete().eq("id", created.id);
          throw error;
        }
        return created.id;
      } catch (error: unknown) {
        if (pausedBase !== null) {
          await client
            .from("protocolos")
            .update({ status: "ativo", updated_at: new Date().toISOString() })
            .eq("id", pausedBase.id);
        }
        throw error;
      }
    },

    async activateProtocol(protocolId: string, known: Protocol[]): Promise<void> {
      const target = known.find((protocol) => protocol.id === protocolId) ?? null;
      if (target === null)
        throw new PublicAppError("not_found", "O protocolo não está mais disponível.");
      if (target.status === "ativo") return;

      const activeVersion =
        known.find(
          (protocol) =>
            protocol.id !== protocolId &&
            protocol.grupo_versao_id === target.grupo_versao_id &&
            protocol.status === "ativo",
        ) ?? null;
      try {
        if (activeVersion !== null) {
          await withRequestSignal(undefined, async (signal) => {
            const { error } = await client
              .from("protocolos")
              .update({ status: "pausado", updated_at: new Date().toISOString() })
              .eq("id", activeVersion.id)
              .abortSignal(signal);
            raise(error);
          });
        }
        await withRequestSignal(undefined, async (signal) => {
          const { error } = await client
            .from("protocolos")
            .update({ status: "ativo", updated_at: new Date().toISOString() })
            .eq("id", protocolId)
            .abortSignal(signal);
          raise(error);
        });
      } catch (error: unknown) {
        if (activeVersion !== null) {
          await client
            .from("protocolos")
            .update({ status: "ativo", updated_at: new Date().toISOString() })
            .eq("id", activeVersion.id);
        }
        throw error;
      }
    },

    async deleteProtocol(protocolId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("protocolos")
          .delete()
          .eq("id", protocolId)
          .abortSignal(signal);
        raise(error);
      });
    },

  };
}

export type ProtocolsApi = ReturnType<typeof createProtocolsApi>;
